import { Checkbox } from "@blueprintjs/core";
import { DataTable, FieldSet, IDataTableColumn, useGridState } from "nsitools-react";
import * as React from "react";
import { useQuery } from "react-query";

import { MetierLocaliteDto, UserMetierDto, UtilisateurApi } from "../../../api";
import { BooleanColumn } from "../../../components";
import { useAuth } from "../../../contexts";
import { useApiService, useManageError, useTl } from "../../../hooks";
import { ETLCodes } from "../../../locales";

export interface IUserMetierLocalitesGridProps {
  metier: UserMetierDto;
  userId: number;
}

export const UserMetierLocalitesGrid: React.FunctionComponent<IUserMetierLocalitesGridProps> = ({ metier, userId }) => {
  const { t } = useTl();
  const api = useApiService(UtilisateurApi);
  const { user } = useAuth();
  const { manageError } = useManageError();
  const [selectedIDs, setSelectedIDs] = React.useState<number[]>([]);

  const fetchMetiers = React.useCallback(
    () => api.utilisateurGetMetierLocalites({ id: userId, idmetier: metier.idmetier }),
    [api, metier.idmetier, userId]
  );
  const { data, isFetching, isRefetching, refetch } = useQuery(
    ["user-metier-localites", userId, metier.idmetier],
    fetchMetiers
  );

  const tableState = useGridState<MetierLocaliteDto>({
    serverMode: false,
    enableFilter: true,
    pageSize: 15,
    availablePageSizes: [15, 25, 50],
    sortKeys: {
      localite: "ASC",
      codePostal: "ASC"
    },
    storageOptions: {
      columnCustomizationStorageMode: "state"
    }
  });

  React.useEffect(() => {
    if (data) {
      tableState.setData(data);
    }
  }, [data, tableState]);

  const toggleSelection = React.useCallback(
    async (item: MetierLocaliteDto) => {
      if (!selectedIDs.includes(item.idcodePostal)) {
        setSelectedIDs(ids => [...ids, item.idcodePostal]);
      } else {
        setSelectedIDs(ids => ids.filter(id => id !== item.idcodePostal));
      }

      try {
        await api.utilisateurSaveUserMetierLocalite({ id: userId, idmetier: metier.idmetier, MetierLocaliteDto: item });
        refetch();
      } catch (e) {
        manageError(e);
      }
    },
    [api, metier.idmetier, manageError, refetch, selectedIDs, userId]
  );

  const columns = React.useMemo<IDataTableColumn<MetierLocaliteDto>[]>(
    () => [
      {
        computed: true,
        fieldName: "checkboxes",
        autoFitContent: true,
        render: (item: MetierLocaliteDto) =>
          userId === user.iduser ? (
            <Checkbox
              checked={selectedIDs.includes(item.idcodePostal) || item.selected}
              onChange={e => {
                toggleSelection(item);
              }}
            />
          ) : (
            <BooleanColumn value={item.selected} />
          )
      },
      {
        header: () => t(ETLCodes.CodePostal),
        fieldName: "codePostal"
      },
      {
        header: () => t(ETLCodes.Localite),
        fieldName: "localite"
      }
    ],
    [selectedIDs, t, toggleSelection, user.iduser, userId]
  );

  return (
    <FieldSet title={!!data && t(ETLCodes.NbLocalites, { nb: data.length })}>
      <DataTable
        dateFormat="dd/MM/yyyy"
        tableState={tableState}
        loading={isFetching && !isRefetching}
        columns={columns}
        filterMode="OnEnter"
        keyFieldName="idlocalite"
      />
    </FieldSet>
  );
};

export default UserMetierLocalitesGrid;
